import type { membershipBody } from '@porotutu/api/features/adm/memberships/model'
import type { membershipResource } from '@porotutu/api/features/adm/memberships/resource'
import type { createBody, passwordBody, updateBody } from '@porotutu/api/features/adm/users/model'
import type {
  historyEntryResource,
  managedUserResource,
  passwordSetupResource,
} from '@porotutu/api/features/adm/users/resource'
import { crudQueries, exportHrefFor, staleAfterWrite, unwrap } from '@porotutu/react/api'
import type { ApiError } from '@porotutu/react/errors'
import type { ListMeta, ListQuery } from '@porotutu/react/paging'
import { mutationOptions, type QueryClient, queryOptions } from '@tanstack/react-query'
import { adm } from '@/api/client'
import { ORGANIZATIONS, USERS } from '@/api/keys'
import type { ManagedUserSort, Realm, RoleName } from '../vocabulary'

export type ManagedUser = ReturnType<typeof managedUserResource>
export type Membership = ReturnType<typeof membershipResource>
export type HistoryEntry = ReturnType<typeof historyEntryResource>
export type PasswordSetupState = ReturnType<typeof passwordSetupResource>
export type ManagedUserPage = { data: ManagedUser[]; meta: ListMeta }

export type CreateUserBody = typeof createBody.static
export type UpdateUserBody = typeof updateBody.static
export type PasswordBody = typeof passwordBody.static
export type MembershipBody = typeof membershipBody.static

export type ManagedUserListQuery = ListQuery<ManagedUserSort, never> & {
  realm: Realm
  role?: RoleName
}

const crud = crudQueries<
  ManagedUser,
  HistoryEntry,
  UpdateUserBody,
  CreateUserBody,
  ManagedUserListQuery
>({ prefix: USERS, routes: adm.users })

export const usersQuery = crud.listQuery
export const userQuery = crud.recordQuery
export const userHistoryQuery = crud.historyQuery
export const createUserMutation = crud.createMutation
export const updateUserMutation = crud.updateMutation
export const deleteUserMutation = crud.deleteMutation
export const seedUser = crud.seed

export const membershipsQuery = (id: string) =>
  queryOptions({
    queryKey: [...USERS, 'memberships', id] as const,
    queryFn: async () => unwrap(await adm.users({ id }).memberships.get()).data,
    staleTime: 30_000,
  })

const seedMemberships = (queryClient: QueryClient, id: string, rows: Membership[]) => {
  queryClient.setQueryData(membershipsQuery(id).queryKey, rows)
}

export const setRoleMutation = (queryClient: QueryClient, id: string) =>
  mutationOptions<Membership[], ApiError, MembershipBody>({
    mutationKey: [...USERS, 'memberships', 'set', id],
    mutationFn: async (body) => unwrap(await adm.users({ id }).memberships.put(body)).data,
    onSuccess: (rows) => seedMemberships(queryClient, id, rows),
    onSettled: staleAfterWrite(queryClient, USERS, [ORGANIZATIONS]),
  })

export const revokeRoleMutation = (queryClient: QueryClient, id: string) =>
  mutationOptions<Membership[], ApiError, void>({
    mutationKey: [...USERS, 'memberships', 'remove', id],
    mutationFn: async () => unwrap(await adm.users({ id }).memberships.delete()).data,
    onSuccess: (rows) => seedMemberships(queryClient, id, rows),
    onSettled: staleAfterWrite(queryClient, USERS, [ORGANIZATIONS]),
  })

export const setPasswordMutation = (queryClient: QueryClient, id: string) =>
  mutationOptions<ManagedUser, ApiError, PasswordBody>({
    mutationKey: [...USERS, 'password', id],
    mutationFn: async (body) => unwrap(await adm.users({ id }).password.patch(body)),
    onSuccess: (row) => crud.seed(queryClient, row),
  })

export const PASSWORD_SETUP_POLL = 3_000
export const PASSWORD_SETUP_DEADLINE = 120_000

export const passwordSetupAbandoned = (state: PasswordSetupState) => {
  if (state.waiting === 0 || state.oldest_queued_at === null) return false

  return Date.now() - Date.parse(state.oldest_queued_at) >= PASSWORD_SETUP_DEADLINE
}

export const passwordSetupQuery = (id: string) =>
  queryOptions({
    queryKey: [...USERS, 'password-setup', id] as const,
    queryFn: async () => unwrap(await adm.users({ id })['password-setup'].get()),
    staleTime: 0,
    refetchInterval: (query) => {
      const state = query.state.data

      if (state === undefined || state.waiting === 0) return false

      return passwordSetupAbandoned(state) ? false : PASSWORD_SETUP_POLL
    },
  })

export const sendPasswordSetupMutation = (queryClient: QueryClient, id: string) =>
  mutationOptions<{ accepted: boolean }, ApiError, void>({
    mutationKey: [...USERS, 'password-setup', 'send', id],
    mutationFn: async () => unwrap(await adm.users({ id })['password-setup'].post()),
    onSettled: () => queryClient.invalidateQueries({ queryKey: passwordSetupQuery(id).queryKey }),
  })

export const exportHref = exportHrefFor<ManagedUserListQuery>('/api/v1/adm/users/export', {
  always: ['realm'],
  viewed: ['search', 'role'],
})
